import {
  Anchor,
  Message,
  MessageContainer,
  MessageContent,
  MessageHeader,
  MessageLineItem,
  MessageLinks,
  MessageRow,
} from "./CustomerMessagesStyles";
import LoadingIcon from "../../../common/LoadingIcon";
import React from "react";
import {
  BoardMessage,
  MessageAuthorOptions,
  useGetBoardMessages,
} from "../../../../utils/hooks/commonly-stored/messages";
import { useAppSelector } from "../../../../utils/hooks/store";
import { datetimeStringToHumanReadableDatetime } from "../../../../utils/valueFormatting";
import MessageActions from "./Actions";
import Linkify from "linkify-react";
import Link from "next/link";

export function doesMessageBelongToUser(
  message: BoardMessage,
  userIsAdminOrEngineer: boolean,
) {
  if (userIsAdminOrEngineer) {
    return message.author_type != MessageAuthorOptions.CUSTOMER;
  } else {
    return message.author_type == MessageAuthorOptions.CUSTOMER;
  }
}

function MessageToDisplay({
  message,
  boardId,
}: {
  message: BoardMessage;
  boardId: string;
}) {
  const { isAdminOrEngineer } = useAppSelector((state) => state.auth);
  const { messagesResponse } = useGetBoardMessages(boardId);

  if (!isAdminOrEngineer && message.internal_only) {
    return null;
  }

  const messageBelongsToUser = doesMessageBelongToUser(
    message,
    isAdminOrEngineer,
  );

  return (
    <MessageRow messageBelongsToUser={messageBelongsToUser}>
      <Message messageBelongsToUser={messageBelongsToUser}>
        <div>
          {message.internal_only && (
            <MessageLineItem messageBelongsToUser={messageBelongsToUser}>
              {"INTERNAL MESSAGE"}
            </MessageLineItem>
          )}
          <MessageHeader>
            <MessageLineItem messageBelongsToUser={messageBelongsToUser}>
              {message.author_type === MessageAuthorOptions.SYSTEM ||
              message.author_id === null
                ? MessageAuthorOptions.SYSTEM
                : messagesResponse?.authors[message.author_id]?.email}{" "}
              {`(${datetimeStringToHumanReadableDatetime(message.timestamp)})`}
              {":"}
            </MessageLineItem>
            <MessageActions message={message} boardId={boardId} />
          </MessageHeader>

          <MessageLineItem messageBelongsToUser={messageBelongsToUser}>
            {message.designators.length > 0
              ? `Designator: ${message.designators.join(", ")}`
              : ""}
          </MessageLineItem>
        </div>
        <MessageContent>
          <Linkify options={{ target: "_blank" }}>{message.message}</Linkify>
        </MessageContent>
        {message.file_attachments.length > 0 && (
          <MessageLinks messageBelongsToUser={messageBelongsToUser}>
            Attachments:{" "}
            {message.file_attachments.map((attachment, index) => (
              <Link
                key={`${index}-${attachment.file_name}`}
                href={attachment.link}
              >
                {attachment.file_name.slice(0, 45)}
              </Link>
            ))}
          </MessageLinks>
        )}
      </Message>
    </MessageRow>
  );
}

export default function MessageList({
  boardId,
  maxHeight,
}: {
  boardId: string;
  maxHeight?: string;
}) {
  const { messagesResponse } = useGetBoardMessages(boardId);
  return (
    <MessageContainer maxHeight={maxHeight}>
      {messagesResponse !== null ? (
        messagesResponse.messages.map((message) => {
          return (
            <MessageToDisplay
              key={message.id}
              message={message}
              boardId={boardId}
            />
          );
        })
      ) : (
        <LoadingIcon text={"Loading messages..."} />
      )}
      <Anchor />
    </MessageContainer>
  );
}
